import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import authService from "../services/authService";
import Header from "../components/ui/Header";

const AUTH_KEYS = [
  "authenticated",
  "userId",
  "session_token",
  "userName",
  "userEmail",
];

const hydrateSessionStorage = () => {
  AUTH_KEYS.forEach((key) => {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (!sessionValue && localValue) {
      sessionStorage.setItem(key, localValue);
    }
  });
};

const isUserLoggedIn = () => {
  hydrateSessionStorage();

  const isAuthenticated =
    sessionStorage.getItem("authenticated") === "true" ||
    localStorage.getItem("authenticated") === "true";

  const sessionToken =
    sessionStorage.getItem("session_token") ||
    localStorage.getItem("session_token");

  return isAuthenticated && Boolean(sessionToken);
};

const LoginPage = () => {
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (isUserLoggedIn()) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tempErrors = {};

    if (!formData.identifier) {
      tempErrors.identifier = "Please enter your email or username";
    }

    if (!formData.password) {
      tempErrors.password = "Please enter your password";
    } else if (formData.password.length < 8) {
      tempErrors.password = "Password must be at least 8 characters";
    }

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { response, data } = await authService.login(formData);

      if (response.ok) {
        setIsSuccess(true);

        setTimeout(() => {
          navigate("/home", { replace: true });
        }, 1500);
      } else {
        setIsLoading(false);
        setErrors({
          server: data.error || "Invalid credentials, please try again.",
        });
      }
    } catch (err) {
      setIsLoading(false);
      setErrors({
        server: "An error occurred during login. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      <Header />

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-10">
          <h2 className="text-3xl font-extrabold text-[#0F172A] text-center mb-8">
            Sign in to DEEBug
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.server && (
              <p className="text-[#EF4444] text-base text-center mb-4 font-bold">
                {errors.server}
              </p>
            )}

            <div>
              <label
                htmlFor="identifier"
                className="block text-base font-medium text-[#0F172A] mb-2"
              >
                Email or Username
              </label>
              <input
                type="text"
                id="identifier"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  errors.identifier
                    ? "border-red-500 focus:ring-red-200"
                    : "border-[#E2E8F0] focus:ring-[#0EA5E9]/80"
                }`}
                autoComplete="username"
              />
              {errors.identifier && (
                <p className="text-red-500 text-xs mt-1 font-bold">
                  {errors.identifier}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-base font-medium text-[#0F172A] mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  errors.password
                    ? "border-red-500 focus:ring-red-200"
                    : "border-[#E2E8F0] focus:ring-[#0EA5E9]/80"
                }`}
                autoComplete="current-password"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1 font-bold">
                  {errors.password}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || isSuccess}
              className={`w-full py-2.5 rounded-md font-semibold transition flex items-center justify-center gap-2 ${
                isSuccess
                  ? "bg-green-500 text-white"
                  : "bg-[#0EA5E9] text-white hover:bg-[#0284C7]"
              } ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {isLoading && !isSuccess && (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              {isSuccess
                ? "Logged in! Redirecting..."
                : isLoading
                  ? "Signing in..."
                  : "Log In"}
            </button>
          </form>

          <div className="mt-8 text-center space-y-2">
            <p className="text-[#334155]">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/signup")}
                className="text-[#0EA5E9] font-medium hover:underline"
              >
                Sign up
              </button>
            </p>
            <p className="text-[#334155]">
              Forgot password?{" "}
              <button
                onClick={() => navigate("/reset-password-request")}
                className="text-[#0EA5E9] font-medium hover:underline"
              >
                Reset it
              </button>
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-[#F1F5F9] border-t border-[#E2E8F0] py-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between px-6 gap-4 text-[#475569] text-base">
          <p className="font-medium">
            © {currentYear} DEEBug™. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <Link to="/privacy" className="hover:text-[#0EA5E9] transition font-medium">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[#0EA5E9] transition font-medium">
              Terms
            </Link>
            <Link to="/security" className="hover:text-[#0EA5E9] transition font-medium">
              Security
            </Link>
            <Link to="/contact" className="hover:text-[#0EA5E9] transition font-medium">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;